'use client';

import { useState } from 'react';

import { QueryCache, QueryClient, QueryClientProvider, MutationCache } from '@tanstack/react-query';
import { captureError } from '@/lib/posthog/captureError';

import { shouldSkipError } from './utils';
// import { ReactQueryDevtools } from '@tanstack/react-query-devtools';

interface ReactQueryProviderProps {
  children: React.ReactNode;
}

export const ReactQueryProvider = ({ children }: ReactQueryProviderProps) => {
  const [queryClient] = useState(
    () =>
      new QueryClient({
        queryCache: new QueryCache({
          onError: (error, query) => {
            if (error.message.includes('Failed to fetch')) {
              error.message = `${error.message}: ${query.queryKey}`
            }

            console.log(`>>> tanstack query error`, {
              willSkip: shouldSkipError(error, query.queryKey),
              errorMessage: error.message,
              query,
            });

            if (shouldSkipError(error, query.queryKey)) {
              return;
            }

            captureError(error, {
              source: 'TANSTACK_QUERY_ERROR',
              queryKey: query.queryKey,
              additionalContext: {
                queryHash: query.queryHash,
                status: query.state.status,
                dataUpdateCount: query.state.dataUpdateCount,
                errorUpdatedAt: query.state.errorUpdatedAt,
                fetchFailureCount: query.state.fetchFailureCount,
              },
            });
          },
        }),
        mutationCache: new MutationCache({
          onError: (error, variables, context, mutation) => {
            console.log(`>>> tanstack mutation error`, {
              willSkip: shouldSkipError(error, mutation.options.mutationKey),
              errorMessage: error.message,
              context,
              mutation,
              variables,
            });

            if (shouldSkipError(error, mutation.options.mutationKey)) {
              return;
            }

            captureError(error, {
              source: 'TANSTACK_MUTATION_ERROR',
              additionalContext: {
                mutationKey: mutation.options.mutationKey,
                status: mutation.state.status,
                failureCount: mutation.state.failureCount,
                submittedAt: mutation.state.submittedAt,
                meta: mutation.options.meta,
                variables,
              },
            });
          },
        }),
      }),
  );

  return (
    <QueryClientProvider client={queryClient}>
      {children}
      {/* {process.env.NEXT_PUBLIC_NODE_ENV === 'development' && <ReactQueryDevtools initialIsOpen={false} />} */}
    </QueryClientProvider>
  );
};
